import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SimpleAuthProvider } from './contexts/SimpleAuthContext';
import Navbar from './components/Navbar';
import { LangProvider } from './i18n/lang';
import SimpleLogin from './pages/SimpleLogin';

import SimpleProtectedRoute from './components/SimpleProtectedRoute';
import MaintenanceCheck from './components/MaintenanceCheck';
import ClientBooking from './pages/ClientBooking';
import { trackPageView } from './services/firebase';
import CookieConsentBanner from './components/CookieConsentBanner';

// Eliminado helper con window.location.replace para evitar cierres inesperados en móviles

// Componente de carga visible para evitar pantallas en blanco en lazy-load
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Cargando…</p>
    </div>
  </div>
);

// Lazy loading para las páginas de administrador
const AdminDayDashboard = React.lazy(() => import('./pages/AdminDayDashboard'));
const AdminAppointments = React.lazy(() => import('./pages/AdminAppointments'));
const AdminAccounting = React.lazy(() => import('./pages/AdminAccounting'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminServices = React.lazy(() => import('./pages/AdminServices'));
const AdminEmployees = React.lazy(() => import('./pages/AdminEmployees'));
const AdminCuadrante = React.lazy(() => import('./pages/AdminCuadrante'));
const AdminReports = React.lazy(() => import('./pages/AdminReports'));
// SISTEMA ANTERIOR DESACTIVADO
// const AdminAvailability = React.lazy(() => import('./pages/AdminAvailability'));
const AdminAppConfig = React.lazy(() => import('./pages/AdminAppConfig'));
const AdminDateControl = React.lazy(() => import('./pages/AdminDateControl'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const GlobalAdminDashboard = React.lazy(() => import('./pages/GlobalAdminDashboard'));
const MigrationPage = React.lazy(() => import('./pages/MigrationPage'));
const BusinessRegistration = React.lazy(() => import('./pages/BusinessRegistration'));
const BusinessBooking = React.lazy(() => import('./pages/BusinessBooking'));
// UserRegistration ya no se usa - los clientes entran por /booking/:tenantId
const AdminRedirect = React.lazy(() => import('./pages/AdminRedirect'));
const MakeGlobalAdminPage = React.lazy(() => import('./pages/MakeGlobalAdminPage'));
const DebugClaimsPage = React.lazy(() => import('./pages/DebugClaimsPage'));
const SubscriptionSuccess = React.lazy(() => import('./pages/SubscriptionSuccess'));
const SubscriptionCancel = React.lazy(() => import('./pages/SubscriptionCancel'));
const StripeMigrationPage = React.lazy(() => import('./pages/StripeMigrationPage'));

// Super Admin Pages
const SuperAdminDashboard = React.lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const SuperAdminBusinesses = React.lazy(() => import('./pages/superadmin/SuperAdminBusinesses'));
const SuperAdminConfig = React.lazy(() => import('./pages/superadmin/SuperAdminConfig'));
const SuperAdminLogs = React.lazy(() => import('./pages/superadmin/SuperAdminLogs'));
const SuperAdminNotifications = React.lazy(() => import('./pages/superadmin/SuperAdminNotifications'));
const SuperAdminMaintenance = React.lazy(() => import('./pages/superadmin/SuperAdminMaintenance'));

// Pequeño componente para registrar vistas de página en cambios de ruta
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    console.log('🔴 RouteTracker: pathname changed to:', location.pathname);
    try { trackPageView(location.pathname); } catch { }
  }, [location.pathname]);
  return null;
}

function App() {
  // Silenciador global para mensajes molestos específicos
  try {
    const originalSuccess = toast.success;
    // Evitar duplicar el parche
    if (!toast.__patchedSuccess) {
      toast.success = (content, options) => {
        try {
          const text = typeof content === 'string' ? content.toLowerCase() : '';
          if (text.includes('turno asignado')) {
            return; // silenciar
          }
        } catch { }
        return originalSuccess(content, options);
      };
      toast.__patchedSuccess = true;
    }
  } catch { }
  return (
    <SimpleAuthProvider>
      <LangProvider>
        <Router>
          <RouteTracker />
          <MaintenanceCheck>
            <div className="min-h-screen bg-gray-50">
              <Navbar />
              <div className="mt-6 md:mt-12">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<SimpleLogin />} />
                    <Route path="/landing" element={React.createElement(require('./pages/Landing').default)} />
                    <Route path="/login" element={<SimpleLogin />} />
                    {/* Eliminado duplicado de /booking/:tenantId más abajo. Mantener solo una definición */}
                    <Route
                      path="/booking"
                      element={(() => {
                        console.log('🔵 App.js: Renderizando ruta /booking');
                        return (
                          <SimpleProtectedRoute roles={['client', 'admin']}>
                            <ClientBooking />
                          </SimpleProtectedRoute>
                        );
                      })()}
                    />
                    <Route
                      path="/admin/day"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminDayDashboard />
                        </SimpleProtectedRoute>
                      }
                    />

                    {/* Rutas de Administrador Protegidas y con Lazy Loading */}
                    <Route
                      path="/admin"
                      element={<AdminRedirect />}
                    />
                    <Route
                      path="/admin/appointments"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminAppointments />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/accounting"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminAccounting />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminUsers />
                        </SimpleProtectedRoute>
                      }
                    />
                    {/* Ruta eliminada: /admin/inventory */}
                    <Route
                      path="/admin/services"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminServices />
                        </SimpleProtectedRoute>
                      }
                    />
                    {/* SISTEMA ANTERIOR DESACTIVADO - Solo usar AdminDateControl */}
                    {/* <Route
          path="/admin/availability"
          element={
            <SimpleProtectedRoute roles={['admin']}>
              <AdminAvailability />
            </SimpleProtectedRoute>
          }
        /> */}
                    <Route
                      path="/admin/config"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminAppConfig />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/employees"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminEmployees />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cuadrante"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminCuadrante />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminReports />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/date-control"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <AdminDateControl />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/support"
                      element={
                        <SimpleProtectedRoute roles={['admin']}>
                          <SupportPage />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/global-admin"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <GlobalAdminDashboard />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/migration"
                      element={<MigrationPage />}
                    />
                    {/* Nuevas rutas para multi-negocio */}
                    <Route
                      path="/register-business"
                      element={<BusinessRegistration />}
                    />
                    {/* /register-user redirige a login - los clientes entran por el link del negocio */}
                    <Route
                      path="/register-user"
                      element={<Navigate to="/login" replace />}
                    />
                    <Route
                      path="/booking/:tenantId"
                      element={<BusinessBooking />}
                    />
                    {/* Compatibilidad: redirigir /admin-register a /register-business */}
                    <Route
                      path="/admin-register"
                      element={<Navigate to="/register-business" replace />}
                    />
                    {/* Ruta temporal para hacer administrador global */}
                    <Route
                      path="/make-global-admin"
                      element={<MakeGlobalAdminPage />}
                    />
                    {/* Ruta de debug para claims */}
                    <Route
                      path="/debug-claims"
                      element={<DebugClaimsPage />}
                    />
                    {/* Rutas de Stripe */}
                    <Route
                      path="/subscription-success"
                      element={<SubscriptionSuccess />}
                    />
                    <Route
                      path="/subscription-cancel"
                      element={<SubscriptionCancel />}
                    />
                    <Route
                      path="/stripe-migration"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <StripeMigrationPage />
                        </SimpleProtectedRoute>
                      }
                    />
                    {/* Super Admin Panel - Nuevo panel mejorado */}
                    <Route
                      path="/super/dashboard"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminDashboard />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/super/businesses"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminBusinesses />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/super/config"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminConfig />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/super/logs"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminLogs />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/super/notifications"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminNotifications />
                        </SimpleProtectedRoute>
                      }
                    />
                    <Route
                      path="/super/maintenance"
                      element={
                        <SimpleProtectedRoute roles={['globalAdmin']}>
                          <SuperAdminMaintenance />
                        </SimpleProtectedRoute>
                      }
                    />
                    {/* Redirect /super to /super/dashboard */}
                    <Route
                      path="/super"
                      element={<Navigate to="/super/dashboard" replace />}
                    />
                  </Routes>
                </Suspense>
              </div>
              <CookieConsentBanner />
              <ToastContainer position="top-right" autoClose={1800} hideProgressBar closeOnClick pauseOnHover={false} pauseOnFocusLoss={false} newestOnTop limit={1} />
            </div>
          </MaintenanceCheck>
        </Router>
      </LangProvider>
    </SimpleAuthProvider>
  );
}

export default App;

